import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { TokenService } from 'src/app/_services/token.service';
import { UploadFile } from 'src/app/upload/models/FileOfUpload';
import { Upload, UploadOtp } from 'src/app/upload/models/Upload';
import { environment } from 'src/environments/environment';
import { TenantOtpService } from './tenant-otp.service';

@Injectable({
  providedIn: 'root',
})
export class UploadOtpService {
  // Response loaded by validateCode() for the route guard, handed once to filesByCode()
  private validatedUploads?: any;

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private tenantOtpService: TenantOtpService
  ) {}

  createUpdateUpload(upload: UploadOtp) {
    upload.userId = this.tenantOtpService.getUserId();
    upload.source = this.tenantOtpService.getSource();
    upload.tenant = this.tenantOtpService.getTenant();
    upload.UploadLinkId = this.tenantOtpService.getLinkId();
    const header = this.getheaderWithCode();

    return this.http.post<Upload>(environment.apiUrl + 'uploadXmlWithCode', upload, header);
  }

  uploadFiles(files: UploadFile[]) {
    const header = this.getheaderWithCode();
    return this.http.post(environment.apiUrl + 'uploadFilesWithCode', files, header);
  }

  /**
   * Uploads of the current code. Uses the response that validateCode() loaded for the route guard once, so
   * opening the upload page sends a single UploadsByCode request; later calls load fresh data.
   */
  filesByCode(): Observable<any> {
    const validatedUploads = this.validatedUploads;
    this.validatedUploads = undefined;

    return validatedUploads !== undefined ? of(validatedUploads) : this.getUploadsByCode();
  }

  validateCode(): Observable<boolean> {
    return this.getUploadsByCode().pipe(
      tap((res) => (this.validatedUploads = res)),
      catchError(() => of(false)),
      map((res) => !!res)
    );
  }

  private getUploadsByCode(): Observable<any> {
    const header = this.getheaderWithCode();
    return this.http.get<any>(environment.apiUrl + 'UploadsByCode', header);
  }

  getUploadById(uploadId: string) {
    const header = this.getheaderWithCode();
    return this.http.get<Upload>(environment.apiUrl + 'uploadXmlByIdWithCode/' + uploadId, header);
  }

  changeStatusFiles(files: UploadFile[]) {
    const header = this.getheaderWithCode();
    return this.http.put<string>(environment.apiUrl + 'ChangeStatusFilesWithCode', files, header);
  }

  sendFileToAI(fileIds: string[], type: number, dataType: number) {
    const header = {
      headers: new HttpHeaders().set('Authorization', `Basic ${localStorage.getItem('otp-register-code') || ''}`),
    };
    const data = {
      FileIds: fileIds,
      Tenant: this.tenantOtpService.getTenant(),
      Source: this.tenantOtpService.getSource(),
      UploadFilesType: dataType,
      DataType: type,
    };
    return this.http.post<string>(environment.upload.AIUrlUnregister, data, header);
  }

  checkUploadName(name: string = '') {
    const header = this.getheaderWithCode();
    return this.http.get<boolean>(
      `${environment.apiUrl}CheckUploadNameWithCode/${this.tenantOtpService.getTenant()}/${name}-Extern`,
      header
    );
  }

  deleteUploadsByCode(ids: string[]) {
    return forkJoin(ids.map((id) => this.deleteUploadByCode(id)));
  }

  deleteUploadByCode(id: string) {
    const header = this.getheaderWithCode();
    return this.http.delete(`${environment.apiUrl}DeleteUploadByCode/${id}`, header);
  }

  private getheaderWithCode() {
    return {
      headers: new HttpHeaders()
        .set('Code', localStorage.getItem('otp-code') || '')
        .set('Tenant', this.tenantOtpService.getTenant())
        .set('Source', this.tenantOtpService.getSource()),
    };
  }
}
